<template>
  <div id="import-hike-dialog-backdrop" class="modal fade" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-md modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-body p-5">
          <div class="import-dialog">
            <header>
              <h3><strong>{{ $t('import_hike.dialog.import_hikes') }}</strong></h3>
            </header>
            <div class="file-drop-area rounded-lg p-3 d-flex flex-column align-items-center"
                 @drop.prevent="handleFileDrop"
                 @dragover.prevent>
              <p class="mb-3">{{ $t('import_hike.dialog.drag_drop_gpx_or_fit_files') }}</p>
              <input ref="fileInput"
                     type="file"
                     @change="handleFileChange"
                     multiple
                     accept=".gpx,.fit"
                     hidden>
              <button class="browse-button btn btn-sm btn-secondary" @click="triggerFileInput">{{ $t('shared.choose_files') }}</button>
            </div>
            <ul class="file-list mt-3">
              <li v-for="(file, index) in files" :key="index" class="file-item">
                <span class="file-name">{{ file.name }}</span>
                <button class="remove-btn" @click="removeFile(index)" :title="$t('shared.remove')">&times;</button>
              </li>
            </ul>
            <!-- Single Import Button -->
            <div class="w-100 d-flex justify-content-center mt-3">
              <button class="btn btn-sm btn-primary" v-if="files.length > 0 && !isImporting"
                      @click="importAllFiles">
                {{ $t('import_hike.dialog.import_files') }}
              </button>
              <div v-else-if="isImporting" class="loading">{{ $t('import_hike.dialog.importing') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import FitParser from 'fit-file-parser';
import graph_client from "../../utils/graphql/graph_client";
import { CreateHikeWithPlannedTrails } from "../../utils/graphql/hikes-graph";
import { GpxImportError, GpxImportErrorCode } from "../../utils/helpers/gpxImportError";
import { gpxImportErrorMessage } from "../../utils/helpers/gpxImportErrorMessage";
import { getElevationData } from "../../utils/helpers/HiikerElevationApiHelper";
// Day-splitting, stats and payload shaping live in the helper so they are unit-tested
// without a file input, the elevation service or Apollo (gpxHikeImport.test.ts).
import {
  buildCreateHikeInput,
  calculateDistances,
  dayTitle,
  parseGpxSegments,
  processTrackData,
} from "../../utils/helpers/gpxHikeImport";
import { titleFromFileName } from "../../utils/helpers/titleFromFileName";

export default {
  data() {
    return {
      files: [],
      isImporting: false,
    };
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    removeFile(index) {
      this.files.splice(index, 1);
    },
    handleFileChange(event) {
      const filesAdded = Array.from(event.target.files);
      this.files.push(...filesAdded.map(file => ({
        name: file.name,
        file: file,
      })));
    },
    handleFileDrop(event) {
      const filesAdded = Array.from(event.dataTransfer.files);
      this.files.push(...filesAdded.map(file => ({
        name: file.name,
        file: file,
      })));
    },
    async importAllFiles() {
      if (this.isImporting) return;
      this.isImporting = true;

      try {
        for (const fileEntry of this.files) {
          await this.importFile(fileEntry);
        }

        // Reload the hikes list
        window.location.href = '/user/hikes';
      } catch (error) {
        console.error('Import error:', error);
        if (error instanceof GpxImportError) {
          alert(gpxImportErrorMessage(error));
        } else {
          alert(`Failed to import files: ${error.message}`);
        }
      } finally {
        this.isImporting = false;
      }
    },
    async importFile(fileEntry) {
      try {
        const file = fileEntry.file;
        const fallbackTitle = titleFromFileName(file.name);
        const extension = file.name.split('.').pop().toLowerCase();
        let parsedFile;

        if (extension === 'gpx') {
          parsedFile = await this.parseGpxFile(file, fallbackTitle);
        } else if (extension === 'fit') {
          parsedFile = await this.parseFitFile(file, fallbackTitle);
        } else {
          throw new Error('Unsupported file type');
        }

        const { hikeTitle, segments } = parsedFile;
        if (!segments.length) {
          throw new GpxImportError(GpxImportErrorCode.NoRouteOrTrack);
        }

        // One planned trail per leg of the file. A HiiKER hike exports one <rte> per day, so
        // this is what makes a multi-day route (the Wicklow Way) import whole instead of as
        // its first day.
        const days = [];
        for (let index = 0; index < segments.length; index++) {
          const segment = segments[index];
          const day = processTrackData(segment.points, dayTitle(hikeTitle, segment, index, segments.length));
          await this.applyCorrectedElevation(day);
          days.push(day);
        }

        const variables = {
          input: buildCreateHikeInput({ hikeTitle, fileName: file.name, days })
        };

        const response = await graph_client.request(CreateHikeWithPlannedTrails, variables);

        if (response.createHike?.errors?.length > 0) {
          const errors = response.createHike.errors.map((e: any) =>
            `${e.fieldName}: ${e.errors.join(', ')}`
          ).join('\n');
          throw new Error(`Errors creating hike:\n${errors}`);
        }

        if (!response.createHike?.hike?.id) {
          throw new Error('Unknown error occurred while creating hike');
        }
      } catch (error) {
        console.error('Import error:', error);
        throw error;
      }
    },
    // Replace the file's own elevations with the elevation service's, in place. Left as-is
    // when the service is unavailable or disagrees on the point count — the import still
    // goes through on the file's own numbers. Distances are recomputed either way, since a
    // correction changes the coordinates.
    async applyCorrectedElevation(day) {
      const coordsForElevationAPI = day.coordinates.map(coord => [coord[1], coord[0]]); // [lat, lng]
      const elevationData = await getElevationData(coordsForElevationAPI);

      if (elevationData && elevationData.length === day.coordinates.length) {
        day.coordinates = elevationData.map(e => [e.lng, e.lat, e.elevation]);
        day.altitudes = elevationData.map(e => e.elevation);
      }

      day.distances = calculateDistances(day.coordinates);
    },
    async parseGpxFile(file, fallbackTitle) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            resolve(parseGpxSegments(String(e.target.result ?? ''), fallbackTitle));
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(new GpxImportError(GpxImportErrorCode.Unreadable));
        reader.readAsText(file);
      });
    },
    // A FIT file is one recorded activity, so it is always a single day.
    async parseFitFile(file, fallbackTitle) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            new FitParser({
              force: true,
              speedUnit: 'm/s',
            }).parse(e.target.result, (error, data) => {
              if (error) return reject(error);

              const points = data.records
                .filter(r => r.position_lat && r.position_long)
                .map(r => ({
                  lat: r.position_lat,
                  lon: r.position_long,
                  elevation: r.altitude || 0,
                }));

              resolve({ hikeTitle: fallbackTitle, segments: points.length >= 2 ? [{ points }] : [] });
            });
          } catch (error) {
            reject(error);
          }
        };
        reader.readAsArrayBuffer(file);
      });
    },
  },
};
</script>

<style scoped>
.file-drop-area {
  border: 2px dashed var(--gray-border);
  padding: 10px;
  text-align: center;
  margin: 20px 0;
}

.file-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  margin-bottom: 6px;
  gap: 10px;
}

.file-name {
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.remove-btn {
  background: none;
  border: none;
  color: var(--bs-secondary, #6c757d);
  font-size: 1.2rem;
  line-height: 1;
  padding: 0 2px;
  flex-shrink: 0;
  opacity: 0.6;
}

.remove-btn:hover {
  opacity: 1;
  color: var(--bs-danger, #dc3545);
}

button {
  cursor: pointer;
}

.loading {
  color: #555;
}
</style>
